/**
 * Feature Flags Configuration
 * 
 * Purpose: Enable parallel development and safe rollout of UI Consolidation
 * 
 * Usage:
 *   import { isFeatureEnabled } from '@/config/featureFlags';
 *   if (isFeatureEnabled('UNIFIED_WORK_QUEUE')) { ... }
 * 
 * Rollout Process:
 *   1. Development: Set flag to true locally
 *   2. Testing: Enable for test environment
 *   3. Beta: Enable for specific users (future enhancement)
 *   4. Full: Set to true for everyone
 *   5. Cleanup: Remove flag after stable
 */

export const FEATURE_FLAGS = {
  // ===== Stream 5: Client Portal =====
  // Unifies ClientUploadPortal + ClientClarificationPortal into UnifiedClientPortal
  CLIENT_PORTAL_UNIFIED: false,

  // ===== Stream 1: Data Specialist =====
  // Merges DataSpecialistQueue + TransactionReviewQueue + IssueTracker into UnifiedWorkQueue
  UNIFIED_WORK_QUEUE: true,

  // ===== Stream 2: Accountant =====
  // Merges AccountantDashboard + AccountantQueue into AccountantCasesUnified
  ACCOUNTANT_UNIFIED_DASHBOARD: false,
  // Hides attorney-focused sections in AccountantReportView
  ACCOUNTANT_SIMPLIFIED_REPORT: false,
  // Shows new Stress Test Summary card
  ACCOUNTANT_STRESS_TEST_CARD: false,

  // ===== Stream 4: Admin/Analytics =====
  // Combines AnalyticsPage + CleanupMonitoring + VendorIntelligence into tabs
  ANALYTICS_HUB_TABS: false,
  // Adds issue counts to Orchestrator pipeline stages
  ORCHESTRATOR_ISSUES_MERGED: false,

  // ===== Stream 3: Attorney =====
  // Integrates Settlement Intelligence into Case Detail
  ATTORNEY_INTEGRATED_SETTLEMENT: false,
  // Auto-populates all tools from case context
  ATTORNEY_AUTO_POPULATE: false,
  // Adds deadline alerts and insights to Attorney Dashboard
  ATTORNEY_DASHBOARD_INSIGHTS: false,
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/**
 * Check if a feature flag is enabled
 * 
 * @param flag - The feature flag key to check
 * @returns boolean - Whether the feature is enabled
 * 
 * @example
 * if (isFeatureEnabled('UNIFIED_WORK_QUEUE')) {
 *   return <UnifiedWorkQueue />;
 * }
 */
export function isFeatureEnabled(flag: FeatureFlagKey): boolean {
  // For now, return static value
  // Future: Could check localStorage, user preferences, or remote config
  return FEATURE_FLAGS[flag];
}

/**
 * Get all feature flags (useful for debugging)
 */
export function getAllFeatureFlags(): typeof FEATURE_FLAGS {
  return { ...FEATURE_FLAGS };
}

/**
 * Feature flag descriptions for documentation/UI
 */
export const FEATURE_FLAG_DESCRIPTIONS: Record<FeatureFlagKey, string> = {
  CLIENT_PORTAL_UNIFIED: 'Unified client portal for uploads and clarifications',
  UNIFIED_WORK_QUEUE: 'Single queue for all data specialist work items',
  ACCOUNTANT_UNIFIED_DASHBOARD: 'Combined accountant dashboard and case queue',
  ACCOUNTANT_SIMPLIFIED_REPORT: 'Simplified report view without attorney tools',
  ACCOUNTANT_STRESS_TEST_CARD: 'Adversarial analysis summary card',
  ANALYTICS_HUB_TABS: 'Tabbed analytics hub (Business, AI, Vendor)',
  ORCHESTRATOR_ISSUES_MERGED: 'Issue counts in pipeline stages',
  ATTORNEY_INTEGRATED_SETTLEMENT: 'Settlement tools embedded in case detail',
  ATTORNEY_AUTO_POPULATE: 'Auto-populate tools from case data',
  ATTORNEY_DASHBOARD_INSIGHTS: 'Deadline alerts and quick insights',
};
